import { useCallback } from 'react';
import { Task, FullProject, LogEntry, ProjectMetadata } from '../../types';
import { todayStr, addDays } from '../../utils/dateUtils';
import { areProjectIdsMatching } from '../../utils/idNormalizer';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { AIProjectMetadata } from '../../components/ai/planner/types';

export interface SuggestedAiTask {
  id?: string;
  name: string;
  type?: Task['type'];
  durationDays?: number;
  assignee?: string;
  startTime?: string;
  endTime?: string;
  description?: string;
  priority?: Task['priority'];
  mergeMode?: 'create' | 'update' | 'ignore';
  existingTaskId?: string | number;
  dependencies?: string[];
}

export interface AiTaskPlanningConfig {
  planningMode?: 'startDate' | 'endDate' | 'event';
  startDate?: string;
  endDate?: string;
  eventDate?: string;
  prepDays?: number;
}

export interface UseTaskIdeaAndAIProps {
  setAllProjects: (updater: FullProject[] | ((prev: FullProject[]) => FullProject[])) => void;
  currentProjectId: string;
  setTasks: React.Dispatch<React.SetStateAction<Task[]>>;
  setProjectMetadata: React.Dispatch<React.SetStateAction<ProjectMetadata>>;
  saveSnapshot: () => void;
  addLogEntry: (action: LogEntry['action'], description: string, details?: string) => void;
  markProjectDirty: (id: string) => void;
  onMarkTaskDirty: (taskId: string | number, projectId?: string) => void;
  user: { uid?: string; email?: string | null; displayName?: string | null } | null;
  COMM_PROJECT_ID: string;
}

export function useTaskIdeaAndAI({
  setAllProjects,
  currentProjectId,
  setTasks,
  setProjectMetadata,
  saveSnapshot,
  addLogEntry,
  markProjectDirty,
  onMarkTaskDirty,
  user,
  COMM_PROJECT_ID
}: UseTaskIdeaAndAIProps) {

  const upgradeIdeaToTask = useCallback((id: string | number) => {
    saveSnapshot();
    
    setAllProjects(prev => prev.map(p => {
      const taskIdx = p.tasks.findIndex(t => t.id.toString() === id.toString());
      if (taskIdx === -1) return p;
      
      const newTasks = [...p.tasks];
      const existingHistory = newTasks[taskIdx].history || [];
      const historyEntry = {
        id: Math.random().toString(36).substr(2, 9),
        timestamp: Date.now(),
        user: user?.email || 'System',
        field: 'type',
        message: 'Idee zu Aufgabe hochgestuft'
      };
      newTasks[taskIdx] = { 
        ...newTasks[taskIdx], 
        type: 'task', 
        status: 'open',
        start: '',
        end: '',
        parentId: undefined,
        updatedAt: Date.now(),
        history: [historyEntry, ...existingHistory].slice(0, 100)
      };
      
      markProjectDirty(p.id);
      onMarkTaskDirty(id, p.id);
      return { ...p, tasks: newTasks, lastModified: Date.now() };
    }));

    setTasks(prev => {
      const idx = prev.findIndex(t => t.id.toString() === id.toString());
      if (idx === -1) return prev;
      const next = [...prev];
      const existingHistory = next[idx].history || [];
      const historyEntry = {
        id: Math.random().toString(36).substr(2, 9),
        timestamp: Date.now(),
        user: user?.email || 'System',
        field: 'type',
        message: 'Idee zu Aufgabe hochgestuft'
      };
      next[idx] = { 
        ...next[idx], 
        type: 'task', 
        status: 'open', 
        start: '', 
        end: '', 
        parentId: undefined,
        history: [historyEntry, ...existingHistory].slice(0, 100)
      };
      return next;
    });

    addLogEntry('update', "Idee zur Aufgabe befördert");
  }, [saveSnapshot, addLogEntry, markProjectDirty, onMarkTaskDirty, setAllProjects, setTasks, user?.email]);

  const handleAddAITasks = useCallback((
    projectId: string, 
    suggestedTasks: SuggestedAiTask[], 
    config: AiTaskPlanningConfig, 
    description: string, 
    answers: string, 
    metadata?: AIProjectMetadata | Partial<ProjectMetadata> | Record<string, unknown>, 
    extractedDocumentText?: string,
    inboxItem?: {
      id?: string | number;
      type?: string;
      title?: string;
      preview?: string;
      date?: string;
      linkType?: string;
      content?: string;
    },
    meetingLog?: { title: string; content: string; timestamp: number }
  ) => {
    addLogEntry('ai_plan', `${suggestedTasks.length} Aufgaben durch KI hinzugefügt für Projekt ${projectId}`);
    
    const fullDescription = `Idee: ${description}\n\nZusatzinfos: ${answers}`;
    const importDate = new Date().toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    const docSection = extractedDocumentText
      ? `\n\n---\n## 📄 Importierte Unterlagen (${importDate})\n\n${extractedDocumentText}`
      : '';

    setAllProjects(prev => prev.map(p => {
      if (p.id !== projectId) return p;

      // Update metadata
      const currentMeta = p.metadata || {};
      const { stakeholders: rawAiStakeholders, ...restAiMeta } = (metadata || {}) as Record<string, unknown>;
      const updatedMetadata: ProjectMetadata = {
        ...currentMeta,
        description: currentMeta.description ? `${currentMeta.description}\n\n${fullDescription}` : fullDescription,
        report: currentMeta.report ? `${currentMeta.report}${docSection}` : docSection.trimStart(),
        ...restAiMeta,
      };

      if (Array.isArray(rawAiStakeholders)) {
        updatedMetadata.stakeholders = rawAiStakeholders;
      } else if (typeof rawAiStakeholders === 'string' && rawAiStakeholders.trim()) {
        const existing = Array.isArray(currentMeta.stakeholders) ? currentMeta.stakeholders : [];
        const parsedNames = rawAiStakeholders.split(/[,;\n]/).map(s => s.trim()).filter(Boolean);
        const newEntries = parsedNames.map((name, i) => ({ id: `ai-sh-${Date.now()}-${i}`, name, role: 'Stakeholder' }));
        updatedMetadata.stakeholders = [...existing, ...newEntries];
      }

      if (inboxItem) {
        if (inboxItem.type === 'photo') {
          const gallery = currentMeta.gallery || [
            { id: 'g1', title: 'Kick-off Workshop', url: 'https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?auto=format&fit=crop&q=80&w=400', date: '14.05.2026' },
            { id: 'g2', title: 'Team Meeting', url: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=400', date: '15.05.2026' }
          ];
          gallery.push({
            id: String(inboxItem.id),
            title: inboxItem.title || 'Foto',
            url: inboxItem.preview || 'https://images.unsplash.com/photo-1531538606174-0f90ff5dce83?auto=format&fit=crop&q=80&w=400',
            date: inboxItem.date || new Date().toLocaleDateString('de-DE')
          });
          updatedMetadata.gallery = gallery;
        } else if (inboxItem.type === 'link') {
          const pressEcho = currentMeta.pressEcho || [
            { id: 'p1', title: 'Bericht Bezirksblatt: "GWantt revolutioniert Planung"', source: 'meinbezirk.at', date: '28.04.2024', type: 'press' },
            { id: 'p2', title: 'Video: ORF Tirol heute - Beitrag zum Kick-off', source: 'ORF TVthek', date: '25.04.2024', type: 'video' }
          ];
          pressEcho.push({
            id: String(inboxItem.id),
            title: inboxItem.title || 'Link',
            type: inboxItem.linkType === 'video' ? 'video' : 'press',
            source: inboxItem.linkType === 'video' ? 'ORF TVthek' : 'meinbezirk.at',
            date: inboxItem.date || new Date().toLocaleDateString('de-DE'),
            content: inboxItem.content
          });
          updatedMetadata.pressEcho = pressEcho;
        } else {
          // audio or email
          const processedMemos = currentMeta.processedMemos || [];
          processedMemos.push({
            id: inboxItem.id,
            title: inboxItem.title,
            type: inboxItem.type,
            content: inboxItem.content,
            date: inboxItem.date || new Date().toLocaleDateString('de-DE')
          });
          updatedMetadata.processedMemos = processedMemos;
        }
      }

      if (meetingLog) {
        const meetingLogs = currentMeta.meetingLogs || [];
        meetingLogs.push({
          id: `meet-${Date.now()}`,
          title: meetingLog.title,
          content: meetingLog.content,
          timestamp: meetingLog.timestamp || Date.now()
        });
        updatedMetadata.meetingLogs = meetingLogs;
      }

      // Calculate tasks
      let currentStart = todayStr;
      if (config.planningMode === 'startDate') currentStart = config.startDate || todayStr;
      else if (config.planningMode === 'endDate') {
        const totalDuration = suggestedTasks.reduce((sum: number, t: SuggestedAiTask) => sum + (t.type === 'task' ? (t.durationDays || 1) : 0), 0);
        currentStart = addDays(config.endDate || todayStr, -totalDuration);
      } else if (config.planningMode === 'event') {
        currentStart = addDays(config.eventDate || todayStr, -(config.prepDays || 0));
      }

      const tasksToAdd: Task[] = [];
      const updatedExistingTasksMap = new Map<string, Partial<Task>>();

      suggestedTasks.forEach((st: SuggestedAiTask, idx: number) => {
        const start = currentStart;
        const end = st.type === 'milestone' ? start : addDays(start, st.durationDays || 1);
        currentStart = end;

        if (st.mergeMode === 'update' && st.existingTaskId) {
          updatedExistingTasksMap.set(String(st.existingTaskId), {
            name: st.name,
            start,
            end,
            type: st.type || 'task',
            assignee: st.assignee || undefined,
            startTime: st.startTime || undefined,
            endTime: st.endTime || undefined,
            description: st.description || undefined,
            priority: st.priority || 'medium'
          });
        } else if (st.mergeMode === 'ignore') {
          // Skip
        } else {
          tasksToAdd.push({
            id: st.id || `ai-${Date.now()}-${idx}-${Math.random().toString(36).substr(2, 9)}`,
            name: st.name,
            start,
            end,
            type: st.type || 'task',
            completed: false,
            assignee: st.assignee || undefined,
            startTime: st.startTime || undefined,
            endTime: st.endTime || undefined,
            description: st.description || undefined,
            status: 'open',
            priority: st.priority || 'medium'
          });
        }
      });

      // Link dependencies
      tasksToAdd.forEach((task) => {
        const st = suggestedTasks.find((s: SuggestedAiTask) => s.name === task.name);
        if (st && st.dependencies && Array.isArray(st.dependencies)) {
          task.dependencies = st.dependencies
            .map((depName: string) => {
              const inNew = tasksToAdd.find(nt => nt.name === depName);
              if (inNew) return inNew.id;
              const inExisting = (p.tasks || []).find(et => et.name === depName);
              if (inExisting) return inExisting.id;
              return undefined;
            })
            .filter((id: string | number | undefined): id is string | number => id !== undefined);
        }
      });

      // Assign sortOrder values
      const maxSortOrder = (p.tasks || []).reduce((max: number, t: Task) => Math.max(max, t.sortOrder || 0), 0);
      tasksToAdd.forEach((task, index) => {
        task.sortOrder = maxSortOrder + (index + 1) * 1000;
      });

      const updatedTasks = (p.tasks || []).map((t: Task) => {
        const updates = updatedExistingTasksMap.get(String(t.id));
        if (updates) {
          return {
            ...t,
            ...updates
          };
        }
        return t;
      });

      // Project association: Generated tasks stay in their target project
      const projectTasksToAdd: Task[] = tasksToAdd.map(t => ({
        ...t,
        projectId: p.id
      }));

      if (projectTasksToAdd.length > 0) {
        projectTasksToAdd.forEach(c => {
          setDoc(doc(db, 'tasks', c.id.toString()), c).catch(err => console.warn('Failed to save task:', err));
        });
      }

      const finalTasks = [...updatedTasks, ...projectTasksToAdd];
      
      // If this is the current project, also update the local state
      if (areProjectIdsMatching(projectId, currentProjectId)) {
        setTasks(finalTasks);
        setProjectMetadata(updatedMetadata);
      }

      // Mark project dirty for cloud sync
      markProjectDirty(projectId);
      finalTasks.forEach(t => onMarkTaskDirty(t.id, projectId));

      return {
        ...p,
        tasks: finalTasks,
        metadata: updatedMetadata,
        lastModified: Date.now()
      };
    }));
    
    saveSnapshot();
  }, [addLogEntry, currentProjectId, markProjectDirty, onMarkTaskDirty, setAllProjects, setTasks, setProjectMetadata, saveSnapshot]);

  return {
    upgradeIdeaToTask,
    handleAddAITasks
  };
}
